import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, isValidElement, useCallback } from "react";
import { useUniqueId } from "../hooks/useUniqueId";
import * as Styled from "./ActionListItem.styled";
export const ActionListItem = forwardRef((props, ref) => {
    const { label, icon, href, id, actions, className, isLastItem, disabled = false, "aria-disabled": ariaDisabled, layout = "start", onClick, onKeyDown, ...rest } = props;
    const hasChildren = Boolean(actions);
    const hasIcon = Boolean(icon);
    const labelId = useUniqueId("l-", id);
    const itemProps = hasChildren
        ? { role: "presentation", as: "h4" } // h4 used for styling
        : {
            role: "menuitem",
            "aria-disabled": ariaDisabled ?? disabled,
            "data-actionlist-focusitem": "true",
            ...(href && { as: "a", href })
        };
    const handleClick = useCallback((e) => {
        if (disabled) {
            // prevent shouldCloseMenuOnInteraction on disabled item
            e.stopPropagation();
        }
        else {
            onClick?.(e);
        }
    }, [disabled, onClick]);
    const handleKeyDown = useCallback((e) => {
        if (disabled && (e.key === " " || e.key === "Enter")) {
            // prevent the button click
            e.preventDefault();
            // prevent shouldCloseMenuOnInteraction
            e.stopPropagation();
        }
        else {
            onKeyDown?.(e);
        }
    }, [disabled, onKeyDown]);
    return (_jsxs(Styled.ActionItem, { ref: ref, role: "none", hasChildren: hasChildren, "data-testid": "ActionListItem", className: className, isLastItem: isLastItem, hasIcon: hasIcon, children: [label && (_jsxs(Styled.ActionButton, { ...itemProps, id: hasChildren ? labelId : id, hasIcon: hasIcon, palette: "interactiveConstructive", layout: layout, onClick: handleClick, onKeyDown: handleKeyDown, ...rest, children: [layout === "end" && icon, label, layout === "start" && icon] })), actions && _jsx(ActionListSubGroup, { "aria-labelledby": labelId, layout: layout, actions: actions })] }));
});
export const ActionListSubGroup = (props) => {
    const { actions, actionsHaveIcons, layout = "start", ...rest } = props;
    const hasIcons = actionsHaveIcons || actions.some((action) => action && action.icon);
    const actionsLength = actions.length;
    return (_jsx(Styled.ActionListSubGroup, { role: "group", "data-testid": "ActionListSubGroup", hasIcons: hasIcons, layout: layout, ...rest, children: actions.map((action, index) => {
            if (isValidElement(action)) {
                return action;
            }
            const { label, icon, actions, disabled, ...restOfAction } = action;
            return (_jsx(ActionListItem, { label: label, icon: icon, actions: actions, isLastItem: actionsLength - 1 === index, disabled: disabled, layout: layout, ...restOfAction }, typeof label === "string" ? `${label}_${index}` : index));
        }) }));
};
ActionListItem.displayName = "ActionListItem";
